import React, { createContext, ReactNode, useContext, useMemo, useReducer } from 'react'

export interface BasicModalState {
  isOpen: boolean
}

type ModalState<T = {}> = T & BasicModalState

type ModalsState<T = {}> = Record<keyof T, ModalState<T[keyof T]>>

type State<T = {}> = {
  modals: ModalsState<T>
}

enum ActionTypes {
  SET_VALUE,
  OPEN_MODAL,
  CLOSE_MODAL,
}

type ActionWithPayload<T, K> = { type: T; payload: K }

type Action<T> = T extends ActionTypes.SET_VALUE
  ? ActionWithPayload<T, { modalId: string; value: object }>
  : ActionWithPayload<T, string>

type Dispatch = (action: Action<ActionTypes>) => void

const actions = {
  setValue: (modalId: string, value: object): Action<ActionTypes.SET_VALUE> => ({
    type: ActionTypes.SET_VALUE,
    payload: { modalId, value },
  }),
  openModal: (modalId: string): Action<ActionTypes.OPEN_MODAL> => ({
    type: ActionTypes.OPEN_MODAL,
    payload: modalId,
  }),
  closeModal: (modalId: string): Action<ActionTypes.CLOSE_MODAL> => ({
    type: ActionTypes.CLOSE_MODAL,
    payload: modalId,
  }),
}

interface IModalStateContext<T, U extends State<T>> {
  modalState: U
}

interface IModalDispatchContext {
  modalDispatch: Dispatch
  modalActions: typeof actions
}

const ModalStateContext = createContext<IModalStateContext<{}, State> | undefined>(undefined)
const ModalDispatchContext = createContext<IModalDispatchContext | undefined>(undefined)

const modalReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.OPEN_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          [action.payload]: { ...state.modals[action.payload], isOpen: true },
        },
      }
    case ActionTypes.CLOSE_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          [action.payload]: { ...state.modals[action.payload], isOpen: false },
        },
      }
    case ActionTypes.SET_VALUE:
      return {
        ...state,
        modals: {
          ...state.modals,
          [action.payload.modalId]: {
            ...state.modals[action.payload.modalId],
            ...action.payload.value,
          },
        },
      }
    default:
      return state
  }
}

const ModalProvider = <T extends State>({
  children,
  initialState,
}: {
  children: ReactNode
  initialState: T
}) => {
  const [modalState, modalDispatch] = useReducer(modalReducer, initialState)
  const state: IModalStateContext<T['modals'], T> = { modalState }
  const dispatch: IModalDispatchContext = useMemo(
    () => ({ modalDispatch, modalActions: actions }),
    []
  )

  return (
    <ModalDispatchContext.Provider value={dispatch}>
      <ModalStateContext.Provider value={state}>{children}</ModalStateContext.Provider>
    </ModalDispatchContext.Provider>
  )
}

const useMapActionsToModal = <T, U extends keyof T>(modalId: U) => {
  const memoized = useMemo(
    () => ({
      openModal: () => actions.openModal(modalId as string),
      closeModal: () => actions.closeModal(modalId as string),
      setValue: (value: Partial<T[U]>) => actions.setValue(modalId as string, value),
    }),
    [modalId]
  )

  return memoized
}

const useModalState = <T, U extends keyof T>(modalId: U) => {
  const stateContext = useContext(ModalStateContext) as IModalStateContext<T, State<T>> | undefined

  if (stateContext === undefined) {
    throw new Error('useModalState must be used within a ModalProvider')
  }

  return stateContext.modalState.modals[modalId] as unknown as T[U]
}

const useModalDispatch = <T, U extends keyof T>(modalId: U) => {
  const dispatchContext = useContext(ModalDispatchContext)

  if (dispatchContext === undefined) {
    throw new Error('useModalDispatch must be used within a ModalProvider')
  }

  return {
    modalDispatch: dispatchContext.modalDispatch,
    modalActions: useMapActionsToModal<T, U>(modalId),
  }
}

const withProvider =
  <T extends State>(Component, initialState: T) =>
  props => {
    return (
      <ModalProvider initialState={initialState}>
        <Component {...props} />
      </ModalProvider>
    )
  }

export { withProvider as withModals, useModalState, useModalDispatch }
